import { z } from 'zod';

import { fg } from '@atlaskit/platform-feature-flags';

import type { JiraAdminAgentPayload } from './jira-admin-agent';

const severitySchema = z.enum(['INFO', 'WARNING', 'ERROR']);

const affectedEntitySchema = z.object({
	entityType: z.enum(['SPACE', 'JQL_FILTER', 'BOARD', 'DASHBOARD']),
	count: z.number(),
	severity: severitySchema.optional(),
});

const blastRadiusSchema = z.object({
	analysisId: z.string().optional(),
	operationId: z.string().optional(),
	summary: z.string().optional(),
	affectedEntities: z.array(affectedEntitySchema).optional(),
});

const metadataSchema = z.record(z.unknown()).optional();

const jiraEntitySchema = z.object({
	id: z.string(),
	name: z.string(),
	iconUrl: z.string().optional(),
});

const entitiesSchema = z.object({
	fields: z.array(jiraEntitySchema).optional(),
	spaces: z.array(jiraEntitySchema).optional(),
	workTypes: z.array(jiraEntitySchema).optional(),
});

const planPayloadSchema = z.object({
	actionType: z.literal('plan'),
	id: z.string(),
	parentId: z.string().optional(),
	payload: z.object({
		title: z.string(),
		subtext: z.string().optional(),
		entities: entitiesSchema.optional(),
		blastRadius: blastRadiusSchema.optional(),
		metadata: metadataSchema,
	}),
});

const fieldOptionSchema = z.object({
	optionId: z.string().optional(),
	optionName: z.string(),
});

const fieldChangeSchema = z.object({
	new: fieldOptionSchema.optional(),
	old: fieldOptionSchema.optional(),
});

const fieldOptionsStepPayloadSchema = z.object({
	actionType: z.literal('step'),
	parentId: z.string().optional(),
	actionSubject: z.literal('field-options'),
	payload: z.object({
		operation: z.enum(['create', 'update', 'disable']),
		changes: z.array(fieldChangeSchema),
		fieldId: z.string(),
		fieldName: z.string(),
		blastRadius: blastRadiusSchema,
		metadata: metadataSchema,
	}),
});

const fieldStepPayloadSchema = z.object({
	actionType: z.literal('step'),
	parentId: z.string().optional(),
	actionSubject: z.literal('field'),
	payload: z.object({
		operation: z.enum(['create', 'delete']),
		fieldId: z.string().optional(),
		fieldName: z.string(),
		fieldOptions: z.array(z.string()).optional(),
		fieldType: z.string().optional(),
		blastRadius: blastRadiusSchema,
		metadata: metadataSchema,
	}),
});

const fieldFieldSchemeStepPayloadSchema = z.object({
	actionType: z.literal('step'),
	parentId: z.string().optional(),
	actionSubject: z.literal('field-field-scheme'),
	payload: z.object({
		operation: z.enum(['create', 'remove']),
		blastRadius: blastRadiusSchema,
		metadata: metadataSchema,
	}),
});

const fieldFieldScreenStepPayloadSchema = z.object({
	actionType: z.literal('step'),
	parentId: z.string().optional(),
	actionSubject: z.literal('field-field-screen'),
	payload: z.object({
		operation: z.enum(['create', 'remove']),
		blastRadius: blastRadiusSchema,
		metadata: metadataSchema,
	}),
});

const fieldWorkItemLayoutStepPayloadSchema = z.object({
	actionType: z.literal('step'),
	parentId: z.string().optional(),
	actionSubject: z.literal('field-work-item-layout'),
	payload: z.object({
		operation: z.enum(['create', 'remove']),
		blastRadius: blastRadiusSchema,
		metadata: metadataSchema,
	}),
});

const stepPayloadSchema = z.discriminatedUnion('actionSubject', [
	fieldOptionsStepPayloadSchema,
	fieldStepPayloadSchema,
	fieldFieldSchemeStepPayloadSchema,
	fieldFieldScreenStepPayloadSchema,
	fieldWorkItemLayoutStepPayloadSchema,
]);

const payloadSchema = z.union([planPayloadSchema, stepPayloadSchema]);

/**
 * Parses unknown data into a strongly-typed {@link JiraAdminAgentPayload}.
 *
 * The payload is either a `plan` node, or a `step` node discriminated on
 * `actionSubject` (`field-options`, `field`, `field-field-scheme`,
 * `field-field-screen`, or `field-work-item-layout`). Unknown keys are
 * stripped. Returns `undefined` when the data does not match the expected
 * shape, so callers can gate on a well-formed payload rather than trusting
 * the raw agent/backend data.
 *
 * @param data - Untrusted data to validate (e.g. a message action `data` payload).
 * @returns The parsed `JiraAdminAgentPayload`, or `undefined` if invalid.
 */
export function validateJiraAdminAgentAction(data: unknown): JiraAdminAgentPayload | undefined {
	if (fg('fe_jira_admin_agent_safe_parse_action')) {
		const parsed = payloadSchema.safeParse(data);
		return parsed.success
			? {
					...parsed.data,
					validated: true,
				}
			: {
					...(data as JiraAdminAgentPayload),
					validated: false,
				};
	}

	return data as JiraAdminAgentPayload;
}
